import { useEffect } from "react";
import { onBlocked } from "./editModeState";
import { useEditMode } from "./EditModeContext";

const READ_ONLY_ALLOW_ATTRIBUTE = "data-edit-mode-read-only-allow";
const POLARIS_SELECT_MENU_SELECTOR = ".polarisSelectMenu";

const FORM_CONTROL_SELECTOR = [
  "input",
  "textarea",
  "select",
  "[contenteditable='true']",
  "[contenteditable='']",
  "[role='checkbox']",
  "[role='radio']",
  "[role='switch']",
  "[role='textbox']",
  "[role='combobox']",
  "[role='spinbutton']",
  "[role='slider']",
  POLARIS_SELECT_MENU_SELECTOR,
].join(",");

const CLICK_MUTATING_INPUT_TYPES = new Set([
  "checkbox",
  "radio",
  "file",
  "color",
  "date",
  "datetime-local",
  "month",
  "time",
  "week",
  "range",
]);

const TEXT_ENTRY_INPUT_TYPES = new Set([
  "",
  "email",
  "number",
  "password",
  "search",
  "tel",
  "text",
  "url",
]);

const MUTATING_COMMAND_KEYS = new Set(["Backspace", "Delete", "Enter"]);
const MUTATING_META_KEYS = new Set(["v", "x", "y", "z"]);
const SELECT_MUTATING_KEYS = new Set([
  " ",
  "Enter",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "End",
  "Home",
  "PageDown",
  "PageUp",
]);

export const EditModeInteractionGate = () => {
  const { isEditMode } = useEditMode();

  useEffect(() => {
    if (isEditMode) {
      return;
    }

    // Read-only/edit mode: stop form controls before local UI state mutates.
    const blockReadOnlyInteraction = (event: Event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      onBlocked();
    };

    const handleClick = (event: MouseEvent) => {
      const control = getReadOnlyControl(event.target);
      if (control && isImmediateMutationControl(control)) {
        blockReadOnlyInteraction(event);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      const control = getReadOnlyControl(event.target);
      if (control && isMutatingKey(event, control)) {
        blockReadOnlyInteraction(event);
      }
    };

    const handleTextMutation = (event: Event) => {
      const control = getReadOnlyControl(event.target);
      if (control && isTextEntryControl(control)) {
        blockReadOnlyInteraction(event);
      }
    };

    const handleChange = (event: Event) => {
      const control = getReadOnlyControl(event.target);
      if (control) {
        blockReadOnlyInteraction(event);
      }
    };

    const handleSubmit = (event: SubmitEvent) => {
      const target = event.target;
      if (target instanceof Element && target.closest(`[${READ_ONLY_ALLOW_ATTRIBUTE}]`)) {
        return;
      }
      blockReadOnlyInteraction(event);
    };

    const handleWheel = (event: WheelEvent) => {
      const control = getReadOnlyControl(event.target);
      if (control && isWheelMutableControl(control)) {
        blockReadOnlyInteraction(event);
      }
    };

    document.addEventListener("click", handleClick, true);
    document.addEventListener("keydown", handleKeyDown, true);
    document.addEventListener("beforeinput", handleTextMutation, true);
    document.addEventListener("paste", handleTextMutation, true);
    document.addEventListener("drop", handleTextMutation, true);
    document.addEventListener("input", handleTextMutation, true);
    document.addEventListener("change", handleChange, true);
    document.addEventListener("submit", handleSubmit, true);
    document.addEventListener("wheel", handleWheel, true);

    return () => {
      document.removeEventListener("click", handleClick, true);
      document.removeEventListener("keydown", handleKeyDown, true);
      document.removeEventListener("beforeinput", handleTextMutation, true);
      document.removeEventListener("paste", handleTextMutation, true);
      document.removeEventListener("drop", handleTextMutation, true);
      document.removeEventListener("input", handleTextMutation, true);
      document.removeEventListener("change", handleChange, true);
      document.removeEventListener("submit", handleSubmit, true);
      document.removeEventListener("wheel", handleWheel, true);
    };
  }, [isEditMode]);

  return null;
};

const getReadOnlyControl = (target: EventTarget | null): HTMLElement | null => {
  if (!(target instanceof Element)) {
    return null;
  }

  const label = target.closest("label");
  const labelControl = label instanceof HTMLLabelElement ? label.control : null;
  const control = target.closest(FORM_CONTROL_SELECTOR) ?? labelControl;

  if (!(control instanceof HTMLElement)) {
    return null;
  }

  if (control.closest(`[${READ_ONLY_ALLOW_ATTRIBUTE}]`) || isDisabledOrReadOnly(control)) {
    return null;
  }

  return control;
};

const isDisabledOrReadOnly = (control: HTMLElement) => {
  if (control.closest("fieldset[disabled], [aria-disabled='true']")) {
    return true;
  }

  if (
    (control instanceof HTMLInputElement ||
      control instanceof HTMLSelectElement ||
      control instanceof HTMLTextAreaElement ||
      control instanceof HTMLButtonElement) &&
    control.disabled
  ) {
    return true;
  }

  return (
    (control instanceof HTMLInputElement || control instanceof HTMLTextAreaElement) &&
    control.readOnly
  );
};

const isImmediateMutationControl = (control: HTMLElement) => {
  if (control instanceof HTMLSelectElement || control.matches(POLARIS_SELECT_MENU_SELECTOR)) {
    return true;
  }

  const role = control.getAttribute("role");
  if (role && ["checkbox", "combobox", "radio", "slider", "spinbutton", "switch"].includes(role)) {
    return true;
  }

  return control instanceof HTMLInputElement && CLICK_MUTATING_INPUT_TYPES.has(control.type);
};

const isTextEntryControl = (control: HTMLElement) => {
  if (
    control instanceof HTMLTextAreaElement ||
    control.getAttribute("contenteditable") === "true" ||
    control.getAttribute("contenteditable") === "" ||
    control.getAttribute("role") === "textbox"
  ) {
    return true;
  }

  return control instanceof HTMLInputElement && TEXT_ENTRY_INPUT_TYPES.has(control.type);
};

const isMutatingKey = (event: KeyboardEvent, control: HTMLElement) => {
  if (isImmediateMutationControl(control)) {
    return SELECT_MUTATING_KEYS.has(event.key);
  }

  if (!isTextEntryControl(control)) {
    return false;
  }

  const lowerKey = event.key.toLowerCase();
  if ((event.metaKey || event.ctrlKey) && MUTATING_META_KEYS.has(lowerKey)) {
    return true;
  }

  return (
    MUTATING_COMMAND_KEYS.has(event.key) ||
    (!event.altKey && !event.ctrlKey && !event.metaKey && event.key.length === 1)
  );
};

const isWheelMutableControl = (control: HTMLElement) => {
  const role = control.getAttribute("role");
  return (
    role === "slider" ||
    role === "spinbutton" ||
    (control instanceof HTMLInputElement && ["number", "range"].includes(control.type))
  );
};
